import { useMemo, useState } from "react";
import { defineMessages, type MessageDescriptor, useIntl } from "react-intl";
import { classNames } from "@ct-react/core";
import { useLocaleNavigate } from "@ct-react/locale";
import { actions } from "../../i18n/sharable-defs";
import { Wrapper } from "../layout/wrapper";
import buyIcon from "../../assets/home/prestations/mountain-home.svg";
import vacationIcon from "../../assets/home/prestations/fairy-home.svg";
import renovateIcon from "../../assets/home/prestations/repair-home.svg";
import manageIcon from "../../assets/home/prestations/gerance-immobiliere.svg";
import investIcon from "../../assets/home/prestations/invest-home.svg";
import optimizeIcon from "../../assets/home/prestations/key-home.svg";
import sellIcon from "../../assets/home/prestations/signature.svg";
import solutionIcon from "../../assets/home/prestations/partner.svg";
import "./prestations.scss";

type PrestationKey =
  | "buy"
  | "vacations"
  | "renovate"
  | "manage"
  | "invest"
  | "optimize"
  | "sell"
  | "solution";

type PrestationItem = {
  key: PrestationKey;
  href: string;
  iconUrl: string; // ✅ must be a URL string
  title: MessageDescriptor;
  text: MessageDescriptor;
};

const m = defineMessages({
  kicker: { id: "homepage.prestations.kicker", defaultMessage: "Nos prestations" },
  title: { id: "homepage.prestations.title", defaultMessage: "Vos besoins, nos solutions" },
  buy_title: {
    id: "homepage.prestations.buy-title",
    defaultMessage: "Acheter le chalet de vos rêves en Valais",
  },
  buy_text: {
    id: "homepage.prestations.buy-text",
    defaultMessage:
      "Que vous rêviez d’une résidence principale avec vue imprenable sur les Alpes, d’un chalet de vacances au pied des pistes ou d’un terrain constructible pour un projet sur mesure, nous vous accompagnons à chaque étape avec expertise et passion.",
  },
  vacations_title: {
    id: "homepage.prestations.vacations-title",
    defaultMessage: "Créer vos vacances exclusives sur mesure selon vos envies",
  },
  vacations_text: {
    id: "homepage.prestations.vacations-text",
    defaultMessage:
      "Vous recherchez un séjour inoubliable au cœur du domaine des 4 Vallées ? Notre agence vous invite à découvrir une sélection unique de chalets et d'appartements de luxe ski-in ski-out à Nendaz. Loin des catalogues surchargés, nous avons choisi de nous concentrer sur une collection intime et soignée à laquelle vous pouvez associer nos nombreux services.",
  },

  renovate_title: {
    id: "homepage.prestations.renovate-title",
    defaultMessage: "Rénover votre bien immobilier",
  },
  renovate_text: {
    id: "homepage.prestations.renovate-text",
    defaultMessage:
      "Vous venez d'acquérir un bien immobilier en Valais et vous souhaitez le transformer pour qu'il corresponde à vos rêves ? Notre agence est votre partenaire de confiance pour tous vos projets de rénovation de chalet ou d'appartement à Nendaz. Loin des tracas, nous vous offrons un service complet, de la conception à la réalisation.",
  },

  manage_title: {
    id: "homepage.prestations.manage-title",
    defaultMessage: "Confier la gestion de votre propriété à Haute-Nendaz",
  },
  manage_text: {
    id: "homepage.prestations.manage-text",
    defaultMessage:
      "Libérez-vous des contraintes et confiez-nous la gestion de votre propriété ; nous serons votre seul interlocuteur et ferons intervenir nos partenaires locaux expérimentés et de confiance.",
  },

  invest_title: {
    id: "homepage.prestations.invest-title",
    defaultMessage: "Investir dans l’immobilier Valaisan",
  },
  invest_text: {
    id: "homepage.prestations.invest-text",
    defaultMessage:
      "Le Valais est une destination idéale pour l’achat de résidence secondaire. Nous facilitons le processus, y compris pour les clients étrangers (Lex Koller), et vous proposons des biens alliant plaisir et rentabilité.",
  },

  optimize_title: {
    id: "homepage.prestations.optimize-title",
    defaultMessage: "Optimiser la location courte durée de votre chalet",
  },
  optimize_text: {
    id: "homepage.prestations.optimize-text",
    defaultMessage:
      "Notre agence immobilière indépendante à Nendaz vous offre une approche personnalisée et un service haut de gamme pour assurer une mise en location saisonnière rentable et sereine de votre appartement ou chalet.",
  },

  sell_title: {
    id: "homepage.prestations.sell-title",
    defaultMessage: "Vendre votre propriété avec succès",
  },
  sell_text: {
    id: "homepage.prestations.sell-text",
    defaultMessage:
      "Vendre votre bien d'exception est notre passion. Avec un interlocuteur unique, bénéficiez de notre connaissance intime du marché et d'un catalogue d'acheteurs qualifiés, pour une vente rapide et sereine, du premier contact à la signature notariée.",
  },
  solution_title: {
    id: "homepage.prestations.solution-title",
    defaultMessage: "Pour chaque besoin, une solution",
  },
  solution_text: {
    id: "homepage.prestations.solution-text",
    defaultMessage:
      "Nous nous chargeons de tout pour que vous puissiez passer plus de temps à faire ce dont vous avez envie",
  },
});

const PrestationsPart = ({ className }: { className?: string }) => {
  const intl = useIntl();
  const navigate = useLocaleNavigate();
  const [openKey, setOpenKey] = useState<PrestationKey | null>(null);

  const items: PrestationItem[] = useMemo(() => [
    { key: "buy", href: "/sales/buy-in-switzerland/", iconUrl: buyIcon, title: m.buy_title, text: m.buy_text },
    { key: "vacations", href: "/rentals/experience/", iconUrl: vacationIcon, title: m.vacations_title, text: m.vacations_text },
    { key: "renovate", href: "/renovation/", iconUrl: renovateIcon, title: m.renovate_title, text: m.renovate_text },
    { key: "manage", href: "/services/", iconUrl: manageIcon, title: m.manage_title, text: m.manage_text },
    { key: "invest", href: "/sales/property-development/invest/", iconUrl: investIcon, title: m.invest_title, text: m.invest_text },
    { key: "optimize", href: "/rentals/your-property/", iconUrl: optimizeIcon, title: m.optimize_title, text: m.optimize_text },
    { key: "sell", href: "/with-us/selling/", iconUrl: sellIcon, title: m.sell_title, text: m.sell_text },
    { key: "solution", href: "/agency/", iconUrl: solutionIcon, title: m.solution_title, text: m.solution_text },
  ], []);

  const onToggle = (key: PrestationKey) => setOpenKey((k) => (k === key ? null : key));

  const onNavigate = (href: string) => {
    if (!href) return;
    if (/^https?:\/\//i.test(href)) window.location.href = href;
    else navigate(href);
  };

  // Keep markup classes short; scope everything via the top-level `.hp-prestations`.
  const wrapperClasses = classNames("hp-prestations", className);

  return (
    <section className={wrapperClasses}>
      <Wrapper.Centered className="content">
        <div className="header">
          <span className="kicker">{intl.formatMessage(m.kicker)}</span>
          <h2 className="title">{intl.formatMessage(m.title)}</h2>
        </div>

        <div className="grid">
          {items.map((it) => {
            const isOpen = openKey === it.key;
            return (
              <div key={it.key} className={classNames("tile", { "is-open": isOpen })}>
                <button
                  type="button"
                  className="toggle"
                  onClick={() => onToggle(it.key)}
                  aria-expanded={isOpen}
                  aria-controls={`hp-prestations-panel-${it.key}`}
                >
                  <span
                    className="icon"
                    style={{ ["--hp-prestations-icon" as any]: `url("${it.iconUrl}")` }} // ✅ quotes matter
                    aria-hidden="true"
                  />
                  <span className="tile-title">{intl.formatMessage(it.title)}</span>
                  <span className="chev" aria-hidden="true" />
                </button>

                <div id={`hp-prestations-panel-${it.key}`} className="panel" aria-hidden={!isOpen}>
                  <div className="panel-inner">
                    <p className="text">{intl.formatMessage(it.text)}</p>

                    <button type="button" className="link" onClick={() => onNavigate(it.href)}>
                      {intl.formatMessage(actions.more)}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Wrapper.Centered>
    </section>
  );
};

export default PrestationsPart;
